import { useEffect, useState } from "react";
import { BookOpen, Sparkles, Share2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import { getVerseOfTheDay } from "@/lib/verses";
import VerseCard from "@/components/VerseCard";

export default function VerseOfTheDay() {
  const [verse, setVerse] = useState(getVerseOfTheDay());
  const [visible, setVisible] = useState(true);
  const [cardOpen, setCardOpen] = useState(false);
  const { pathname } = useLocation();
  const dark = pathname.startsWith("/podcasts");

  useEffect(() => {
    // Refresh every hour to catch midnight rollover
    const t = setInterval(() => setVerse(getVerseOfTheDay()), 60 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (!visible) return null;

  const bg = dark
    ? "bg-[#0A0806] text-[#F5EDDE] border-b border-[#E63946]/30"
    : "bg-[#2E4959] text-white border-b border-[#4A6D82]/50";

  return (
    <>
      <div data-testid="verse-of-the-week" className={`relative overflow-hidden ${bg}`}>
        <div className="absolute inset-0 pointer-events-none">
          <div className={`absolute -left-16 top-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl ${dark ? "bg-[#E63946]/15" : "bg-[#7B98A8]/25"}`} />
          <div className={`absolute -right-16 top-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl ${dark ? "bg-[#F4A94E]/15" : "bg-[#C99A5B]/20"}`} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-3 md:py-3.5 flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${dark ? "bg-[#E63946]/20" : "bg-white/15"}`}>
              <BookOpen className={`w-4 h-4 ${dark ? "text-[#E63946]" : "text-[#F4E4C1]"}`} strokeWidth={1.75} />
            </div>
            <span className={`text-[10px] uppercase tracking-[0.3em] whitespace-nowrap ${dark ? "text-[#F4A94E]" : "text-[#F4E4C1]"}`}>
              Verset du jour
            </span>
          </div>

          <div className="flex-1 overflow-hidden">
            <div key={verse.ref} className="jt-fadeUp flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <p className="font-serif text-base md:text-lg italic leading-snug">« {verse.text} »</p>
              <span className={`text-xs md:text-sm whitespace-nowrap ${dark ? "text-[#F4A94E]" : "text-[#F4E4C1]"}`}>
                — {verse.ref}
              </span>
            </div>
          </div>

          <button
            onClick={() => setCardOpen(true)}
            data-testid="verse-share-open"
            aria-label="Voir la carte partageable"
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition ${dark ? "bg-[#E63946]/20 text-[#F4A94E] hover:bg-[#E63946]/30" : "bg-white/15 text-white hover:bg-white/25"}`}
          >
            <Share2 className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Carte</span>
          </button>

          <Sparkles className={`hidden md:block w-4 h-4 shrink-0 ${dark ? "text-[#F4A94E]" : "text-[#F4E4C1]"}`} strokeWidth={1.5} />
          <button
            onClick={() => setVisible(false)}
            aria-label="Fermer le verset"
            data-testid="verse-close"
            className="shrink-0 text-white/50 hover:text-white text-lg leading-none px-2"
          >
            ×
          </button>
        </div>
      </div>

      {cardOpen && <VerseCard verse={verse} onClose={() => setCardOpen(false)} />}
    </>
  );
}
